import { Form, Formik } from "collections/Form";
import { useTranslation } from "utilities/i18n";
import {
  CreateSubnetRuleInput,
  DeleteSubnetRuleInput,
  useUpdateSubnetRuleMutation,
  useCreateSubnetRuleMutation,
  useDeleteSubnetRuleMutation,
  UpdateSubnetRuleInput,
  useGetSubnetRulesQuery
} from "generated/graphql";
import { SettingsHeader } from "./GeneralSiteSettings";
import { SettingsCategory } from "../../Inventory/Settings-Shared/DeviceSettingsComponents";
import { Layout } from "views/Main/Layouts";
import { PolyIcon, LoadingIndicator } from "elements";
import { FieldArray } from "formik";
import { Redirect } from "polyrouter";
import BlockingForm from "collections/Form/BlockingForm";
import { needs, useGuard } from "rbac";
import {
  SubnetMapSchema,
  SubnetMap,
  PublicSubnet as PublicSubnetType
} from "./Types";
import PublicSubnet from "./PublicSubnet";
import { DISMISS, useNotification } from "collections";
import { validateManually } from "./SubnetHelpers";
import { nameIsUnassigned } from "utilities/stringUtils";

const SiteProvisioningSettings: React.FC<{
  id: string;
  name: string;
}> = ({ id: siteId, name: siteName }) => {
  const { Content } = Layout;
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const [, updateSubnetRule] = useUpdateSubnetRuleMutation();
  const [, createSubnetRule] = useCreateSubnetRuleMutation();
  const [, deleteSubnetRule] = useDeleteSubnetRuleMutation();
  const userCanEdit = useGuard(needs("UPDATE_SITES"));

  const { data, fetching, executor: refresh } = useGetSubnetRulesQuery({
    variables: {
      input: { siteId }
    },
    requestPolicy: "network-only"
  });

  if (fetching) {
    return <LoadingIndicator />;
  }

  let subnets: Record<string, PublicSubnetType> = {};
  data?.getSubnetRules?.forEach(({ id: ruleId, publicCidr, privateCidr }) => {
    if (!subnets[publicCidr]) {
      subnets[publicCidr] = {
        cidr: publicCidr,
        ruleId,
        privateSubnets: []
      };
    }

    if (privateCidr) {
      subnets[publicCidr].privateSubnets.push({
        ruleId,
        cidr: privateCidr
      });
    }
  });

  const convertCidrToNumber = (cidr: string) =>
    Number(
      cidr
        .split(/[/.]+/)
        .map(num => `00${num}`.slice(-3))
        .join("")
    );

  Object.values(subnets).forEach(publicSubnet => {
    publicSubnet.privateSubnets = publicSubnet.privateSubnets.sort((a, b) => {
      return convertCidrToNumber(a.cidr) - convertCidrToNumber(b.cidr);
    });
  });

  const initialValues: SubnetMap = {
    publicSubnets: Object.values(subnets).sort((a, b) => {
      return convertCidrToNumber(a.cidr) - convertCidrToNumber(b.cidr);
    }),
    siteId
  };

  if (nameIsUnassigned(siteName)) {
    return <Redirect to="/manage/sites" />;
  }

  const handleSubmit = async (values: SubnetMap) => {
    const cidrs = values.publicSubnets.map(publicSubnet => publicSubnet.cidr);
    const duplicateCidr = cidrs.find(
      (cidr, index) => cidrs.indexOf(cidr) !== index
    );
    if (duplicateCidr) {
      addNotification({
        type: "error",
        message: t("SUBNET_PUBLIC_BLOCK_ERROR", { cidr: duplicateCidr }),
        dismiss: DISMISS.USER_ONLY
      });
      return;
    }

    let subnetRulesToCreate: Array<CreateSubnetRuleInput> = [];
    let subnetRulesToDelete: Array<DeleteSubnetRuleInput> = [];
    let subnetRulesToUpdate: Array<UpdateSubnetRuleInput> = [];

    values.publicSubnets.forEach(
      ({ ruleId: publicSubnetRuleId, cidr: publicCidr, privateSubnets }) => {
        const initialPublicSubnet = initialValues.publicSubnets.find(
          ips => ips.ruleId === publicSubnetRuleId
        );

        //Create all subnet rules if publicSubnet is new
        if (!initialPublicSubnet) {
          if (!privateSubnets.length) {
            subnetRulesToCreate.push({
              siteId,
              publicCidr: publicCidr
            });
          }
          privateSubnets.forEach(({ cidr: privateCidr }) => {
            subnetRulesToCreate.push({
              siteId,
              publicCidr,
              privateCidr
            });
          });
        } else {
          // publicSubnet exists
          let privateSubnetsStartIndex = 0;
          // if only public w/null privateCidr exists, update it with the first new privateCidr
          // then only loop through starting at the second element
          if (
            !initialPublicSubnet.privateSubnets.length &&
            privateSubnets.length
          ) {
            subnetRulesToUpdate.push({
              ruleId: publicSubnetRuleId,
              publicCidr,
              privateCidr: privateSubnets[0].cidr
            });
            privateSubnetsStartIndex = 1;
          }
          // Update/create all if publicCidr was changed
          if (publicCidr !== initialPublicSubnet.cidr) {
            if (!privateSubnets.length) {
              subnetRulesToUpdate.push({
                ruleId: publicSubnetRuleId,
                publicCidr,
                privateCidr: ""
              });
            } else {
              privateSubnets
                .slice(privateSubnetsStartIndex)
                .forEach(({ ruleId, cidr: privateCidr }) => {
                  // create if new rule, else update
                  if (ruleId.startsWith("new-")) {
                    subnetRulesToCreate.push({
                      siteId,
                      publicCidr,
                      privateCidr
                    });
                  } else {
                    subnetRulesToUpdate.push({
                      ruleId,
                      publicCidr,
                      privateCidr
                    });
                  }
                });
            }
          } else {
            // publicCidr was not changed
            privateSubnets
              .slice(privateSubnetsStartIndex)
              .forEach(({ ruleId, cidr: privateCidr }) => {
                const initialPrivateSubnet = initialPublicSubnet.privateSubnets.find(
                  ips => ips.ruleId === ruleId
                );
                if (!initialPrivateSubnet) {
                  subnetRulesToCreate.push({
                    siteId,
                    publicCidr,
                    privateCidr: privateCidr
                  });
                } else if (initialPrivateSubnet.cidr !== privateCidr) {
                  subnetRulesToUpdate.push({
                    ruleId,
                    publicCidr,
                    privateCidr
                  });
                }
              });
          }
        }
      }
    );

    // find subnet rules which were deleted
    initialValues.publicSubnets.forEach(initialPublicSubnet => {
      const publicSubnet = values.publicSubnets.find(
        ps => ps.ruleId === initialPublicSubnet.ruleId
      );

      if (!publicSubnet) {
        if (!initialPublicSubnet.privateSubnets.length) {
          subnetRulesToDelete.push({
            ruleId: initialPublicSubnet.ruleId
          });
        }
        // public subnet and all private subnets were deleted
        initialPublicSubnet.privateSubnets.forEach(({ ruleId }) => {
          subnetRulesToDelete.push({
            ruleId
          });
        });
      } else {
        // if all privateSubnets were deleted,
        // update the first privateCidr to null
        if (
          !publicSubnet.privateSubnets.length &&
          initialPublicSubnet.privateSubnets.length
        ) {
          subnetRulesToUpdate.push({
            ruleId: initialPublicSubnet.privateSubnets[0].ruleId,
            publicCidr: publicSubnet.cidr,
            privateCidr: ""
          });

          // skip the first
          initialPublicSubnet.privateSubnets.slice(1).forEach(privateSubnet => {
            subnetRulesToDelete.push({
              ruleId: privateSubnet.ruleId
            });
          });
        } else {
          // check if each private subnet is deleted
          initialPublicSubnet.privateSubnets.forEach(privateSubnet => {
            const isDeleted = !publicSubnet.privateSubnets.some(
              subnet => subnet.ruleId === privateSubnet.ruleId
            );
            if (isDeleted) {
              subnetRulesToDelete.push({
                ruleId: privateSubnet.ruleId
              });
            }
          });
        }
      }
    });

    if (
      subnetRulesToUpdate.length === 0 &&
      subnetRulesToDelete.length === 0 &&
      subnetRulesToCreate.length === 0
    ) {
      refresh();
      return;
    }

    let updateErrors = await Promise.all(
      subnetRulesToUpdate.map(async subnetRule => {
        const { error } = await updateSubnetRule({
          input: subnetRule
        });
        return error;
      })
    );

    let deleteErrors = await Promise.all(
      subnetRulesToDelete.map(async subnetRule => {
        const { error } = await deleteSubnetRule({
          input: subnetRule
        });
        return error;
      })
    );

    let createErrors = await Promise.all(
      subnetRulesToCreate.map(async subnetRule => {
        const { error } = await createSubnetRule({
          input: subnetRule
        });
        return error;
      })
    );

    const errors = [
      ...updateErrors.filter(e => !!e),
      ...deleteErrors.filter(e => !!e),
      ...createErrors.filter(e => !!e)
    ];

    if (errors.length < 1) {
      addNotification({
        type: "success",
        message: t("SUBNET_RANGES_UPDATED"),
        dismiss: DISMISS.DEFAULT
      });
      refresh();
    }
  };

  return (
    <Content className="settings">
      <Formik
        name="DeviceAssociationSettings"
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={SubnetMapSchema}
        enableReinitialize
      >
        {({ values, dirty, setErrors, errors }) => (
          <BlockingForm unsavedChanges={dirty}>
            <Form className="form grid">
              <SettingsHeader title={siteName} userCanEdit={userCanEdit} />
              <SettingsCategory title={t("DEVICE_ASSOCIATION")}>
                <div className="grid">
                  <p className="pl-3 col-12 col-xl-10">
                    {t("SITE_PROVISIONING_DESCRIPTION")}
                  </p>
                  <FieldArray name="publicSubnets">
                    {arrayHelpers => (
                      <div className="subnets-scroll pl-3 col-12 col-xl-9">
                        {values.publicSubnets.map(
                          ({ ruleId, cidr, privateSubnets }, index) => {
                            const validatePublicSubnet = (index: number) => {
                              validateManually(
                                arrayHelpers.remove,
                                errors,
                                setErrors,
                                index
                              );
                            };
                            return [
                              <PublicSubnet
                                ruleId={ruleId}
                                publicCidr={cidr}
                                fieldName={`${arrayHelpers.name}[${index}]`}
                                index={index}
                                isEditable={userCanEdit}
                                privateSubnets={privateSubnets}
                                remove={validatePublicSubnet}
                                siteId={siteId}
                                key={"public-" + ruleId}
                              />,
                              <hr className="ml-0" key={"hr-" + ruleId} />
                            ];
                          }
                        )}
                        {!values.publicSubnets && (
                          <label>No subnet ranges set for this site</label>
                        )}
                        {userCanEdit && (
                          <button
                            type="button"
                            className="btn-poly-icon public-subnet-add"
                            onClick={() =>
                              arrayHelpers.push({
                                ruleId:
                                  "new-" + (values.publicSubnets.length ?? 0),
                                cidr: "",
                                privateSubnets: []
                              })
                            }
                          >
                            <PolyIcon
                              icon="plus_circle"
                              className="icon-add mr-5"
                            />
                            <div className="add-label">
                              {t("ADD_PUBLIC_SUBNET")}
                            </div>
                          </button>
                        )}
                      </div>
                    )}
                  </FieldArray>
                </div>
              </SettingsCategory>
            </Form>
          </BlockingForm>
        )}
      </Formik>
    </Content>
  );
};

export default SiteProvisioningSettings;
